<template>
  <fragment>
    <div class="gift-note--form-wrapper" :class="{ open }" tabindex="-1">
      <div class="gift-note--form">
        <div class="gift-note--close-container">
          <button
            class="gift-note--close"
            @click="toggleGiftWrap"
            aria-label="close gift wrap"
          >
            <svg-close></svg-close>
          </button>
        </div>

        <div class="gift-wrap-container">
          <img
            class="gift-wrap-image"
            :src="giftWrapCollectionImage || null"
            alt="gift wrap collection image"
          />

          <div class="gift-wrap-info">
            <h4 class="gift-wrap-header">{{ header }}</h4>
            <p class="gift-wrap-text">{{ text }}</p>
          </div>

          <div
            class="gift-wrap-empty"
            v-if="!giftWrapItems || !giftWrapItems.length"
          >
            You have no more items to gift wrap
          </div>

          <ul class="gift-wrap-items">
            <li
              class="gift-wrap-item"
              v-for="(item, index) in giftWrapItems"
              :key="item.id + ':' + index"
              v-show="item.availableQuantity"
            >
              <div class="gift-wrap-image-wrapper">
                <img
                  :src="item.image || item.cartItem.image"
                  :alt="'gift wrap for' + item.name"
                />
              </div>

              <div class="gift-wrap-name">
                {{ item.name }}
              </div>

              <div
                class="gift-wrap-quantity-selector f aic bold lh-10 fs-10 plaid-l"
              >
                <button
                  :aria-label="'decrement ' + item.variant.name"
                  class="cart-quantity old-p-1 bg-dark-beige old-mr-1"
                  :disabled="!item.variant.available"
                  @click="decrementGiftWrap(item)"
                >
                  <div>-</div>
                </button>
                <div class="line-item-quantity bg-dark-beige old-p-1">
                  {{ item.currentQuantity }}
                </div>

                <button
                  :aria-label="'increment ' + item.name"
                  class="cart-quantity old-p-1 bg-dark-beige old-ml-1"
                  :disabled="
                    !item.variant.available ||
                    item.currentQuantity >= item.availableQuantity
                  "
                  @click="incrementGiftWrap(item)"
                >
                  <div>+</div>
                </button>
              </div>

              <div class="gift-wrap-price">
                {{ price(item) }}
              </div>
            </li>
          </ul>

          <p class="gift-wrap-disclaimer">
            {{ disclaimer }} More questions?
            <a aria-label="gift wrap faq" :href="faqLink">Click here.</a>
          </p>

          <cart-gift-note-toggle></cart-gift-note-toggle>
        </div>
      </div>
      <div class="gift-wrap-cta">
        <button
          aria-label="add gift wrapping"
          class="add-to-cart button caps lh-10 fs-12 x plaid-l c-white bg-old-spice shadow shadow-white transition"
          @click="addGiftWrap"
        >
          + ADD TO CART - {{ giftWrapTotalPrice }}
        </button>
      </div>
    </div>
  </fragment>
</template>

<script>
import { mapState, mapActions } from "vuex";
import { formatMoney, currencySymbol } from "@/lib/currency";

export default {
  name: "cart-gift-wrap",
  props: {
    giftWrapCollectionImage: String,
  },
  data() {
    return {
      header: theme.cartConfig.giftWrapHeader || "",
      text: theme.cartConfig.giftWrapText || "",
      faqLink: theme.cartConfig.giftWrapFaqLink || "",
      disclaimer: theme.cartConfig.giftWrapDisclaimer || "",
    };
  },
  computed: {
    ...mapState({
      open: (state) => state.cart.giftWrapOpen,
      cart: (state) => state.cart.data,
      giftWrapItems: (state) => state.cart.giftWrapItems,
      variantsMetafields: (state) => state.metafields.variants,
    }),
    giftWrapTotalPrice() {
      const totalPrice = this.giftWrapItems.reduce((_totalPrice, giftWrap) => {
        return _totalPrice + giftWrap.currentQuantity * giftWrap.variant.price;
      }, 0);

      return `${currencySymbol()}${formatMoney(
        totalPrice,
        "{{ amount_no_trailing_zeroes }}"
      )}`;
    },
  },
  watch: {
    open(giftWrapOpen) {
      if (giftWrapOpen) {
        window.addEventListener("keyup", this.handleEsc);
      } else {
        window.removeEventListener("keyup", this.handleEsc);
      }
    },
    cart: {
      immediate: true,
      handler(cart) {
        const items = cart.items || [];
        if (!items || !items.length) return;
        const giftWrapState = items.reduce((giftWrapItems, cartItem) => {
          const variantMetafields = this.variantsMetafields.find(
            ({ id }) => parseInt(id) === parseInt(cartItem.variant_id)
          );
          if (!variantMetafields || !variantMetafields.gift_wrap_variant)
            return giftWrapItems;
          const giftWrapVariant = variantMetafields.gift_wrap_variant;
          const availableQuantity = this.getRemainingGiftWrapAvailableInCart(
            giftWrapVariant,
            cartItem
          );

          return [
            ...giftWrapItems,
            {
              availableQuantity,
              cartQuantity: this.getGiftWrapQuantityInCart(giftWrapVariant.id),
              currentQuantity: availableQuantity,
              id: giftWrapVariant.id,
              image: variantMetafields.image,
              name: variantMetafields.name,
              cartItem,
              variant: giftWrapVariant,
            },
          ];
        }, []);

        this.updateGiftWrap(giftWrapState);
      },
    },
  },
  methods: {
    ...mapActions({
      toggleGiftWrap: "cart/toggleGiftWrap",
      removeItem: "cart/removeItem",
      updateCart: "cart/updateCart",
      addToCart: "cart/addToCart",
      updateGiftWrap: "cart/updateGiftWrap",
    }),
    decrementGiftWrap({ id: variantId, currentQuantity }) {
      if (!currentQuantity) return;
      const newState = this.giftWrapItems.map(
        ({ id, currentQuantity, ...rest }) => {
          if (variantId === id) {
            return { id, currentQuantity: currentQuantity - 1, ...rest };
          }

          return { id, currentQuantity, ...rest };
        }
      );
      this.updateGiftWrap(newState);
    },
    incrementGiftWrap({ id: variantId, currentQuantity, cartItem }) {
      if (currentQuantity >= cartItem.quantity) return;
      const newState = this.giftWrapItems.map(
        ({ id, currentQuantity, ...rest }) => {
          if (variantId === id) {
            return { id, currentQuantity: currentQuantity + 1, ...rest };
          }

          return { id, currentQuantity, ...rest };
        }
      );
      this.updateGiftWrap(newState);
    },
    async addGiftWrap() {
      const { giftWrapItems } = this;
      if (!giftWrapItems.length) return;
      var hasChanged = false;
      for (let i = 0; i < giftWrapItems.length; i++) {
        const {
          id,
          availableQuantity,
          currentQuantity,
          cartQuantity,
          cartItem,
        } = giftWrapItems[i];
        const reactiveItemId = cartItem.id;
        if (!availableQuantity || !currentQuantity) continue;
        const key = this.getGiftWrapKeyInCart(id);

        if (!cartQuantity && currentQuantity) {
          //  add line item
          await this.addToCart([
            {
              id: parseInt(id),
              quantity: currentQuantity,
              properties: { _reactive: [reactiveItemId] },
            },
          ]);
          if (!hasChanged) hasChanged = true;
        } else {
          // update existing line item
          await this.updateCart({
            key,
            quantity: cartQuantity + currentQuantity,
            properties: {
              _reactive: [reactiveItemId],
              _created_at: Date.now(),
            },
          });
          if (!hasChanged) hasChanged = true;
        }
      }

      if (hasChanged) {
        this.toggleGiftWrap();
      }
    },
    getRemainingGiftWrapAvailableInCart(giftWrapVariant, cartItem) {
      if (!giftWrapVariant.available) return 0;
      const cartQuantity = cartItem.quantity;
      const giftWrapQuantity = this.getGiftWrapQuantityInCart(
        giftWrapVariant.id
      );
      const quantityDiff = cartQuantity - giftWrapQuantity;
      return quantityDiff > 0 ? quantityDiff : 0;
    },
    getGiftWrapInCart(variantId) {
      const cartItems = this.cart.items || [];
      return cartItems.find(({ variant_id }) => variant_id === variantId);
    },
    getGiftWrapQuantityInCart(variantId) {
      const giftWrap = this.getGiftWrapInCart(variantId);
      return giftWrap ? giftWrap.quantity : 0;
    },
    getGiftWrapKeyInCart(variantId) {
      const giftWrap = this.getGiftWrapInCart(variantId);
      return giftWrap ? giftWrap.key : null;
    },
    handleEsc(e) {
      e.stopPropagation();
      if (e.keyCode === 27) this.toggleGiftWrap();
    },
    price(item) {
      return `${currencySymbol()}${formatMoney(
        item.variant.price * item.currentQuantity,
        "{{ amount_no_trailing_zeroes }}",
        false
      )}`;
    },
  },
};
</script>
